import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { IoSettings } from "react-icons/io5";
import { IoIosChatbubbles, IoIosNotifications } from "react-icons/io";
import sitemasterLogo from "../assets/SiteMaster.svg";
import sampleLogo from "../assets/sample_org.png";
import avatar from "../assets/3d_avatar_26.png";
import { BASE_URL, environment } from "../utils/constant";
import ProfileCard from "./ProfileCard";
import SubscriptionModal from "../screens/Dashboard/Subscription/SubscriptionModal";
import Microinteraction from "../assets/Microinteraction.svg";
import NotificationSidePopUp from "./NotificationSidePopUp";
import { rtdb } from "utils/firebase"; // Import the rtdb from your firebase config file
import SettingsDropdown from "./Settings";
import {
  FaChevronDown,
  FaChevronUp,
  FaSortDown,
  FaSortUp,
} from "react-icons/fa";
import OrganizationDropdown from "./OrgDropdown";
import { BsStars } from "react-icons/bs";
import ChatBot from "./Chatbot";
import TaskChatbot from "./TaskChatbot";
import { useNotifications } from "utils/Notifications/NotificationContext";
import { MdOutlineHomeWork, MdPermMedia } from "react-icons/md";
import { GoHome } from "react-icons/go";
// import TaskChatbot from "./TaskBot";

const Header = ({
  organization_name,
  setCurrentTab,
  active,
  flush,
  todayMs,
  prepareForLogout,
  isLogo = false,
  isBell = true,
  isHome = false,
}) => {
  const [showLogout, setShowLogout] = useState(false);
  const [userData, setUserData] = useState();
  const [organization, setOrganization] = useState({});
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  // const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [showChatBot, setShowChatBot] = useState(false);
  const [showTaskChat, setShowTaskChat] = useState(false);
  const [showOrganizationList, setShowOrganizationList] = useState(false);
  const [loadDone, setLoadDone] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(
    localStorage.getItem("hasActiveSubscription") === "true"
  );
  const navigate = useNavigate();
  const profileButtonRef = useRef(null);
  const profileCardRef = useRef(null);
  const { unreadCount, markAllAsRead, markAsRead } = useNotifications();
  const { notifications, requestNotificationPermission } = useNotifications();
  useEffect(() => {
    // Request notification permission on app load
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    const onStorage = () =>
      setHasActiveSubscription(
        localStorage.getItem("hasActiveSubscription") === "true"
      );
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Close mobile menu when resizing to desktop
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePhotoClick = () => {
    setShowLogout(!showLogout);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close profile dropdown when opening mobile menu
    if (!isMobileMenuOpen) {
      setShowLogout(false);
    }
  };

  // Close profile card on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showLogout) return;
      const clickedInsideCard = profileCardRef.current?.contains(event.target);
      const clickedOnButton = profileButtonRef.current?.contains(event.target);
      if (!clickedInsideCard && !clickedOnButton) {
        setShowLogout(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLogout]);

  const handleLogout = async () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    };
    try {
      const response = await fetch(
        `${BASE_URL}/token/blacklist/`,
        requestOptions
      );
      const data = await response.json();
      await prepareForLogout();
      // console.log(data);
      if (data?.code !== "token_not_valid") {
        localStorage.clear();
        navigate("/login");
      } else {
        console.log("Token not valid");
        localStorage.clear();
        navigate("/login");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getUserDetails = async () => {
    const accessToken = localStorage.getItem("access");
    if (accessToken) {
      const response = await fetch(`${BASE_URL}/auth/user-details-of-user/`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      if (!response.ok) {
        // navigate("/profession");
        return;
      }
      const data = await response.json();
      if (data?.status) {
        setUserData(data);
        setIsOwner(data?.data?.profession === "owner");
      } else {
        // navigate("/profession");
      }
    }
  };

  const getOrganizationDetails = async () => {
    const accessToken = localStorage.getItem("access");
    const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (accessToken) {
      const response = await fetch(
        `${BASE_URL}/org/get-a-organization/${organizationId}`,
        {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        }
      );
      const data = await response.json();
      if (data?.status) {
        setOrganization(data?.data);
      }
    }
  };

  const toggleNotifications = () => {
    if (!isNotificationOpen) {
      setShowLogout(false);
    }
    setIsNotificationOpen(!isNotificationOpen);
    // Close other dropdowns when opening notifications
  };

  const fetchOrganizations = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      // if (!accessToken) {
      //   navigate("/login");
      //   return;
      // }

      const response = await fetch(`${BASE_URL}/org/list-user-organization/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();

      if (data.status) {
        setOrganizations(data.data);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  useEffect(() => {
    getUserDetails();
    fetchOrganizations();
  }, [localStorage.getItem("access")]);

  useEffect(() => {
    getOrganizationDetails();
  }, [localStorage.getItem("access"), localStorage.getItem("organization")]);

  // Fetch notifications when organization id is available
  // useEffect(() => {
  //   if (organization?.id) {
  //     fetchNotifications();
  //   }
  // }, [organization]);

  return (
    <>
      <header className="bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-full mx-auto px-2">
          <div className="flex items-center justify-between h-16 px-3 md:px-6">
            {/* Left section - always visible */}
            <div className="flex items-center gap-2 md:gap-8">
              <div
                className="flex items-center gap-4 px-4 justify-center bg-white rounded-lg cursor-pointer"
                style={{
                  display:
                    JSON.parse(localStorage.getItem("organization"))?.id &&
                      !isLogo
                      ? "none"
                      : "flex",
                }}
              >
                <img
                  src={sitemasterLogo}
                  alt="logo"
                  className="h-6 md:h-8 -mt-2 w-auto"
                  draggable={false}
                />
                {isHome && (
                  <div
                    className="bg-[#ECE6F0] rounded-lg p-2 transition-colors"
                    onClick={() => {
                      navigate("/dashboard");
                    }}
                  >
                    <GoHome color="#3B0069" size={20} />
                  </div>
                )}
              </div>

              {/* Organization info - visible on medium screens and up */}
              {windowWidth > 768 &&
                organization?.organization_name &&
                !isLogo && (
                  // <div className="hidden md:flex items-center py-1 px-2 gap-2 rounded-xl border-[1px] border-text-clr">
                  //   {!isOwner &&
                  //     (showOrganizationList ? (
                  //       <FaSortUp
                  //         onClick={() => {
                  //           setShowOrganizationList(false);
                  //         }}
                  //       />
                  //     ) : (
                  //       <FaSortDown
                  //         onClick={() => {
                  //           setShowOrganizationList(true);
                  //         }}
                  //       />
                  //     ))}
                  //   <img
                  //     src={organization?.logo_image_file || sampleLogo}
                  //     alt="logo"
                  //     className="h-6 w-6 md:h-8 md:w-8 rounded-full border-1 border-[#737373]"
                  //   />
                  //   <p
                  //     className="text-[14px] md:text-[18px] font-semibold text-gray-800"
                  //     title={organization?.organization_name}
                  //   >
                  //     {organization?.organization_name?.length >
                  //     (windowWidth > 1024 ? 45 : 20)
                  //       ? `${organization?.organization_name?.slice(
                  //           0,
                  //           windowWidth > 1024 ? 25 : 15
                  //         )}...`
                  //       : organization?.organization_name}
                  //   </p>
                  //   {showOrganizationList && <div></div>}
                  // </div>
                  <OrganizationDropdown
                    organizations={organizations}
                    currentOrganization={organization}
                    {...(isOwner
                      ? {
                        onThreeDotsClick: () => setCurrentTab("Organization"),
                      }
                      : {})}
                    onOrganizationChange={(newOrg) => {
                      setOrganization(newOrg);
                      // Optionally call getOrganizationDetails to refresh data
                      // getOrganizationDetails();
                    }}
                    windowWidth={windowWidth}
                  />
                )}
            </div>

            {/* Mobile menu button - only on small screens */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6 text-[#3B0069]" />
                ) : (
                  <Menu className="h-6 w-6 text-[#3B0069]" />
                )}
              </button>
            </div>

            {/* Right section - visible on medium screens and up */}
            <div className="hidden md:flex items-center gap-3 md:gap-6">
              {/* Search bar */}
              <div className="hidden md:block flex-1 mx-2">
                <div className="relative">
                  {/* <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-[#7A6E82]" />
                  </div> */}
                  {/* <input
                    type="text"
                    placeholder="Search for something"
                    className="pl-10 pr-4 py-2 bg-[#F3F0F5] rounded-3xl text-sm text-[#877A90] focus:outline-none focus:ring-2 focus:ring-[#3B0069] focus:border-transparent transition-all"
                  /> */}
                </div>
              </div>

              {/* {isOwner && (
                <div className="relative">
                  <button
                    className="bg-[#ECE6F0] rounded-lg p-2 transition-colors hover:bg-[#D9D0E6]"
                    onClick={() => {
                      setShowChatBot(!showChatBot);
                      setIsNotificationOpen(false);
                      setShowLogout(false);
                      setShowSettings(false);
                    }}
                  >
                    <BsStars fill="#58267F" className="h-5 w-5" />
                  </button>
                  <ChatBot
                    isOpen={showChatBot}
                    onClose={() => setShowChatBot(false)}
                  />
                </div>
              )} */}
              {/* {isOwner && (
                <div className="relative">
                  <button
                    className="bg-[#ECE6F0] rounded-lg p-2 transition-colors hover:bg-[#D9D0E6]"
                    onClick={() => {
                      setShowChatBot(!showChatBot);
                      setIsNotificationOpen(false);
                      setShowLogout(false);
                      setShowSettings(false);
                    }}
                  >
                    <BsStars fill="#58267F" className="h-5 w-5" />
                  </button>
                  <TaskChatbot
                    isOpen={showChatBot}
                    onClose={() => setShowChatBot(false)}
                  />
                </div>
              )} */}

              {/* Task updates chatbot icon */}
              {/* <div className="relative">
                <button
                  className="bg-[#ECE6F0] rounded-lg p-2 transition-colors hover:bg-[#D9D0E6]"
                  onClick={() => {
                    setShowTaskChat(true);
                    setIsNotificationOpen(false);
                    setShowLogout(false);
                    setShowSettings(false);
                  }}
                  title="Ask about task updates"
                >
                  <BsStars fill="#58267F" className="h-5 w-5" />
                </button>
                <TaskChatbot isOpen={showTaskChat} onClose={() => setShowTaskChat(false)} />
              </div> */}

              {/* Settings button */}
              {/* <button
                className="bg-[#ECE6F0] rounded-lg p-2 transition-colors"
                onClick={() => {
                  setShowSettings(!showSettings);
                  setShowLogout(false);
                }}
              >
                <IoSettings fill="#58267F" className="h-5 w-5" />
              </button> */}
              {/* <SettingsDropdown
                userData={userData?.data}
                handleLogOut={handleLogout}
                refetch={getUserDetails}
              /> */}
              <div className="relative">
                <button
                  className="bg-[#ECE6F0] rounded-lg p-2 transition-colors hover:bg-[#D9D0E6]"
                  onClick={() => {
                    navigate("/dashboard/global-media");
                  }}
                >
                  <MdPermMedia fill="#58267F" className="h-5 w-5" />
                </button>
              </div>
              <div className="relative">
                <button
                  className="bg-[#ECE6F0] rounded-lg p-2 transition-colors hover:bg-[#D9D0E6]"
                  onClick={() => {
                    navigate("/dashboard/global-communication");
                  }}
                >
                  <IoIosChatbubbles fill="#58267F" className="h-5 w-5" />
                </button>
              </div>

              {/* Notifications button */}
              {isBell && (
                <div className="relative">
                  <button
                    className="bg-[#ECE6F0] rounded-lg p-2 transition-colors hover:bg-[#D9D0E6]"
                    onClick={toggleNotifications}
                  >
                    <IoIosNotifications fill="#58267F" className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>
                  <NotificationSidePopUp
                    isOpen={isNotificationOpen}
                    onClose={() => setIsNotificationOpen(false)}
                    notifications={notifications}
                    markAllAsRead={markAllAsRead}
                    markAsRead={markAsRead}
                    currentUser={userData?.data}
                  />
                </div>
              )}

              {false && isOwner && (
                <button
                  className="flex items-center gap-2 p-2 pr-3 bg-[#3B0069] text-white rounded-full transition-all hover:bg-opacity-90"
                  onClick={() => setIsSubscriptionOpen(true)}
                >
                  <img
                    src={Microinteraction}
                    alt="spin"
                    className="h-6 w-6 animate-spin-pause"
                  />
                  <span className="font-medium text-[14px]">
                    {hasActiveSubscription ? "Upgrade" : "Subscription"}
                  </span>
                </button>
              )}

              {/* User profile */}
              <div className="flex items-center">
                <button
                  onClick={handlePhotoClick}
                  ref={profileButtonRef}
                  className="flex items-center gap-3 p-1.5 hover:bg-gray-100
                              rounded-lg transition-colors"
                >

                  {
                    userData?.data?.userpic ? (
                      <img
                        src={userData?.data?.userpic}
                        alt="Profile"
                        style={{
                          objectFit: "cover",
                        }}
                        className="h-8 w-8 rounded-full ring-2 ring-gray-100"
                      />) : (
                      // avatar with initials
                      <div className="p-1 w-8 h-8 rounded-full bg-sm-primary flex items-center justify-center">
                        <span className="text-md font-medium text-white">
                          {userData?.data?.first_name?.charAt(0)}{userData?.data?.last_name?.charAt(0)}
                        </span>
                      </div>
                    )}
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-gray-700">
                      {userData?.data?.first_name} {userData?.data?.last_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {userData?.data?.profession}
                    </span>
                  </div>
                </button>
                {showLogout && (
                  <div
                    className="absolute right-4 mt-10 w-48"
                    ref={profileCardRef}
                  >
                    <ProfileCard
                      handleLogOut={handleLogout}
                      data={userData?.data}
                    />
                  </div>
                )}
              </div>
              {/* <div className="relative">
                <button
                  className="flex items-center gap-3 p-1.5 hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
                  onClick={handlePhotoClick}
                >
                  <img
                    src={userData?.data?.userpic || avatar}
                    alt="User"
                    className="h-8 w-8 md:h-10 md:w-10 rounded-full ring-2 ring-gray-100"
                  />
                </button>
                {showLogout && (
                  <div className="absolute right-0 mt-2 w-48">
                    <ProfileCard
                      handleLogOut={handleLogout}
                      data={userData?.data}
                    />
                  </div>
                )}
              </div> */}
            </div>
          </div>

          {/* Mobile menu - only appears on small screens when menu is open */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 shadow-lg">
              {/* Organization info for mobile */}
              {organization?.organization_name && (
                <div className="flex items-center py-2 px-2 gap-2 mb-4 border-b border-gray-200 pb-4">
                  <img
                    src={organization?.logo_image_file || sampleLogo}
                    alt="logo"
                    className="h-8 w-8 rounded-full border-1 border-[#737373]"
                  />
                  <p
                    className="text-[16px] font-semibold text-gray-800"
                    title={organization?.organization_name}
                  >
                    {organization?.organization_name?.length > 28
                      ? `${organization?.organization_name?.slice(0, 25)}...`
                      : organization?.organization_name}
                  </p>
                </div>
              )}

              {/* Mobile search */}
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-[#7A6E82]" />
                </div>
                <input
                  type="text"
                  placeholder="Search for something"
                  className="w-full pl-10 pr-4 py-2 bg-[#F3F0F5] rounded-3xl text-sm text-[#877A90] focus:outline-none focus:ring-2 focus:ring-[#3B0069] focus:border-transparent transition-all"
                />
              </div>

              {/* Mobile menu items */}
              <div className="flex flex-col space-y-3">
                <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg">
                  <IoSettings fill="#58267F" className="h-5 w-5" />
                  <span className="text-gray-800">Settings</span>
                </div>

                <div
                  className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg"
                  onClick={() => {
                    // For mobile, we'll navigate to a dedicated notifications page
                    // or toggle the modal and close the mobile menu
                    setIsMobileMenuOpen(false);
                    // Allow some time for the mobile menu to close
                    setTimeout(() => {
                      toggleNotifications();
                    }, 100);
                  }}
                >
                  <div className="relative">
                    <IoIosNotifications fill="#58267F" className="h-5 w-5" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                        {notificationCount > 9 ? "9+" : notificationCount}
                      </span>
                    )}
                  </div>
                  <span className="text-gray-800">Notifications</span>
                </div>

                {false && isOwner && (
                  <div
                    className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    onClick={() => {
                      setIsSubscriptionOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <img
                      src={Microinteraction}
                      alt="Microinteraction"
                      className="h-5 w-5 animate-spin-pause"
                    />
                    <span className="text-gray-800">Subscription</span>
                  </div>
                )}

                {/* User profile for mobile */}
                <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg border-t border-gray-200 mt-2 pt-4">
                  <img
                    src={userData?.data?.userpic || avatar}
                    alt="User"
                    className="h-8 w-8 rounded-full ring-1 ring-gray-200"
                  />
                  <div className="flex flex-col">
                    <span className="text-gray-800 font-medium">
                      {userData?.data?.firstname} {userData?.data?.lastname}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {userData?.data?.email}
                    </span>
                  </div>
                </div>

                <button
                  className="w-full py-2 px-4 bg-red-50 text-red-600 rounded-lg font-medium mt-2"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Notification dropdown is now placed directly within the notification button container */}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={isSubscriptionOpen}
        onClose={() => {
          setIsSubscriptionOpen(false);
        }}
      />
    </>
  );
};

export default Header;
